/* eslint-disable */
import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  X,
  Camera,
  Sparkles,
  DollarSign,
  MessageSquarePlus,
  ArrowRight,
  ArrowLeft,
  Package,
  PackagePlus,
  Search,
  Check,
  ImagePlus,
  Wand2,
  Type,
  Minus,
  PartyPopper,
  Maximize2,
  Minimize2,
} from "lucide-react";
import {
  useAIGeneration,
  PIPELINE_STEPS,
  FINISH_STEP_MS,
  resolveFreeRemaining,
} from "../../../context/AIProductGenerationContext";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { updateAccount } from "../../../redux/account/account";

const GENERATION_COST = 10;
const AI_COST = 10;
const MIN_TOKENS_AFTER = -50;

const INSTRUCTION_SUGGESTIONS = [
  "Products.advertorial.suggestion_success_story",
  "Products.advertorial.suggestion_testimonials",
  "Products.advertorial.suggestion_before_after",
  "Products.advertorial.suggestion_limited_offer",
  "Products.advertorial.suggestion_free_delivery",
];

const STEPS_NEW = ["source", "image", "name", "price", "instructions"];
const STEPS_EXISTING = ["source", "selectProduct", "instructions"];

function extractPrice(product) {
  const isPositive = (v) =>
    v !== undefined && v !== null && v !== "" && Number(v) > 0;

  if (isPositive(product?.price)) return product.price;

  if (Array.isArray(product?.offers)) {
    const offer = product.offers.find((o) => o && isPositive(o.price));
    if (offer) return offer.price;
  }

  if (Array.isArray(product?.options)) {
    const option = product.options.find((o) => o && isPositive(o.price));
    if (option) return option.price;
  }

  return null;
}

function extractImage(product) {
  return (
    product?.image ||
    product?.newImage ||
    (Array.isArray(product?.gallery) && product.gallery[0]) ||
    (Array.isArray(product?.newGallery) && product.newGallery[0]) ||
    null
  );
}

function normalizeProduct(raw) {
  return {
    id: raw?.id,
    name: raw?.name || "",
    price: extractPrice(raw),
    currency: raw?.currency || "DZD",
    image: extractImage(raw),
    raw,
  };
}

const initialFormState = {
  productSource: null,
  selectedProduct: null,
  imageFile: null,
  imagePreview: "",
  productName: "",
  price: "",
  currency: "DZD",
  instructions: "",
};

export default function AIAdvertorialPageModal({
  show,
  setShow,
  existingProducts = [],
  onGenerate,
  onConfirm,
  isGenerating = false,
}) {
  const account = useSelector((state) => state.AccountReducer);
  const [freeRemaining, setFreeRemaining] = useState(null);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const aiGen = useAIGeneration();
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState(initialFormState);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [createdProductId, setCreatedProductId] = useState(null);
  const [createdProductImage, setCreatedProductImage] = useState("");

  const fileInputRef = useRef(null);

  const normalizedProducts = useMemo(
    () => (existingProducts || []).map(normalizeProduct),
    [existingProducts],
  );

  const steps = form.productSource === "existing" ? STEPS_EXISTING : STEPS_NEW;
  const stepKey = steps[stepIndex];
  const totalSteps = steps.length;
  const isSourceStep = stepKey === "source";
  const isInstructionsStep = stepKey === "instructions";
  const phase = aiGen.phase;
  const isBusy = aiGen.phase === "generating";

  const subscription = account?.subscription || {};
  const tokens = Number(subscription.tokens || 0);

  const effectiveFree = resolveFreeRemaining(subscription, freeRemaining);
  const willUseFree = effectiveFree > 0;

  useEffect(() => {
    if (!show) return;
    setFreeRemaining(resolveFreeRemaining(account?.subscription || {}));
  }, [
    show,
    account?.subscription?.aiProductPageFreeRemaining,
    account?.subscription?.aiProductPageFreeMonth,
  ]);

  const handleRetryFromInstructions = useCallback(() => {
    setGenerationError("");
    setCreatedProductId(null);
    setCreatedProductImage("");

    if (typeof aiGen.setPhase === "function") {
      aiGen.setPhase("idle");
    }
    if (typeof aiGen.setPipelineDone === "function") {
      aiGen.setPipelineDone([]);
    }
    if (typeof aiGen.setFillProgress === "function") {
      aiGen.setFillProgress(0);
    }
    if (typeof aiGen.setPipelineIndex === "function") {
      aiGen.setPipelineIndex(-1);
    }

    const instructionsIndex = steps.indexOf("instructions");
    setStepIndex(instructionsIndex >= 0 ? instructionsIndex : steps.length - 1);
  }, [aiGen, steps]);

  const resetAll = useCallback(() => {
    setStepIndex(0);
    setForm(initialFormState);
    setShowCloseConfirm(false);
    setGenerationError("");
    setCreatedProductId(null);
    setCreatedProductImage("");
    aiGen.reset();
  }, [aiGen]);

  const close = useCallback(() => {
    setShow(false);
    setTimeout(resetAll, 220);
  }, [setShow, resetAll]);

  const requestClose = useCallback(() => {
    if (aiGen.phase === "generating") {
      setShowCloseConfirm(true);
      return;
    }
    close();
  }, [aiGen.phase, close]);

  const buildPayload = useCallback(() => {
    const base = {
      user: account?.user || "",
      generation_prompt: form.instructions.trim(),
      generation_language: "ar-DZ",
    };

    if (form.productSource === "existing") {
      const selected = form.selectedProduct;
      const currency = selected?.currency || "DZD";
      return {
        ...base,
        product_id: selected?.id ?? null,
        product_name: selected?.name || "",
        product_price:
          selected?.price != null ? `${selected.price} ${currency}` : null,
        product_image: selected?.image ?? null,
      };
    }

    return {
      ...base,
      product_image: form.imageFile,
      product_name: form.productName.trim(),
      product_price: `${form.price.trim()} ${form.currency}`,
    };
  }, [form, account?.user]);

  const startGeneration = useCallback(() => {
    setGenerationError("");
    setCreatedProductId(null);
    setCreatedProductImage("");

    const payload = buildPayload();

    const localFree = resolveFreeRemaining(subscription, freeRemaining);
    const hasFree = localFree > 0;

    if (!hasFree && tokens - AI_COST < MIN_TOKENS_AFTER) {
      setGenerationError("INSUFFICIENT_TOKENS");
      aiGen.setPhase?.("done");
      return;
    }

    aiGen.startGeneration({
      feature: "product_page",
      onFinish: async () => {},
    });

    (async () => {
      try {
        const data = await onGenerate(payload);
        console.log("billing", data?.billing);
        console.log(
          "effectiveFree",
          resolveFreeRemaining(
            {
              ...account.subscription,
              ...(data?.billing && {
                aiProductPageFreeRemaining: data.billing.freeRemaining,
                aiProductPageFreeMonth: data.billing.monthKey,
              }),
            },
            data?.billing?.freeRemaining,
          ),
        );
        if (!data?.ok || !data?.product) {
          throw new Error(
            data?.error || t("Products.advertorial.error_generate_failed"),
          );
        }

        if (data.billing) {
          const monthKey = data.billing.monthKey || undefined;
          dispatch(
            updateAccount({
              subscription: {
                ...(account.subscription || {}),
                ...(typeof data.billing.tokens === "number"
                  ? { tokens: data.billing.tokens }
                  : {}),
                ...(typeof data.billing.freeRemaining === "number"
                  ? { aiProductPageFreeRemaining: data.billing.freeRemaining }
                  : {}),
                ...(monthKey ? { aiProductPageFreeMonth: monthKey } : {}),
              },
            }),
          );
          if (typeof data.billing.freeRemaining === "number") {
            setFreeRemaining(data.billing.freeRemaining);
          }
        }

        const generated = data.product;
        const pageImage =
          generated.image ||
          generated.pageImage ||
          generated.landingImage ||
          (Array.isArray(generated.gallery) && generated.gallery[0]) ||
          "";

        setCreatedProductImage(pageImage || "");

        if (typeof aiGen.completeBuildStep === "function") {
          aiGen.completeBuildStep();
        } else {
          aiGen.setPipelineDone?.(PIPELINE_STEPS.map((s) => s.id));
          aiGen.setFillProgress?.(1);
          aiGen.setPipelineIndex?.(PIPELINE_STEPS.length);
        }

        const confirmResult = await onConfirm?.({
          product_id:
            form.productSource === "existing" ? form.selectedProduct?.id : null,
          product_source: form.productSource,
          product_name: generated.name || payload.product_name || "",
          product_price: payload.product_price,
          image: pageImage,
          short: generated.short || "",
          textEditor: generated.textEditor || "<p><br></p>",
          description: "",
          image_edited: false,
          description_edited: false,
        });

        if (confirmResult?.id) setCreatedProductId(confirmResult.id);

        await new Promise((resolve) => setTimeout(resolve, FINISH_STEP_MS));

        aiGen.setPhase?.("done");
      } catch (err) {
        const isInsufficient =
          err?.response?.data?.code === "INSUFFICIENT_TOKENS" ||
          err?.response?.status === 402 ||
          (typeof err?.message === "string" &&
            err.message.includes("INSUFFICIENT"));

        let errorCode = "GENERIC";
        if (isInsufficient) {
          errorCode = "INSUFFICIENT_TOKENS";
        } else if (
          err?.code === "ERR_NETWORK" ||
          err?.message === "Network Error" ||
          !err?.response
        ) {
          errorCode = "NETWORK";
        } else if (
          err?.code === "ECONNABORTED" ||
          (typeof err?.message === "string" &&
            err.message.toLowerCase().includes("timeout"))
        ) {
          errorCode = "TIMEOUT";
        } else if (err?.response?.status >= 500) {
          errorCode = "SERVER";
        }

        setGenerationError(errorCode);

        if (typeof aiGen.completeAllStepsNow === "function") {
          aiGen.completeAllStepsNow();
        }
        if (typeof aiGen.setPhase === "function") {
          aiGen.setPhase("done");
        }
      }
    })();
  }, [
    form,
    buildPayload,
    onGenerate,
    onConfirm,
    aiGen,
    freeRemaining,
    subscription,
    tokens,
    account,
    dispatch,
    t,
  ]);

  const canContinue = () => {
    if (stepKey === "source") return !!form.productSource;
    if (stepKey === "selectProduct") return !!form.selectedProduct;
    if (stepKey === "image") return !!form.imageFile;
    if (stepKey === "name") return form.productName.trim().length > 0;
    if (stepKey === "price") return form.price.trim().length > 0;
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({
      ...prev,
      imageFile: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };

  const toggleSuggestion = (labelKey) => {
    const label = t(labelKey, { defaultValue: labelKey });
    setForm((prev) => {
      const parts = prev.instructions
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const exists = parts.includes(label);
      const nextParts = exists
        ? parts.filter((p) => p !== label)
        : [...parts, label];
      return { ...prev, instructions: nextParts.join(", ") };
    });
  };

  const chooseSourceAndAdvance = (source) => {
    setForm({
      ...initialFormState,
      productSource: source,
    });
    setStepIndex(1);
  };

  const handleNext = () => {
    if (!canContinue()) return;
    if (isInstructionsStep) {
      startGeneration();
      return;
    }
    if (stepIndex < totalSteps - 1) setStepIndex((s) => s + 1);
  };

  const handleBack = () => {
    if (isBusy || phase === "done") return;
    if (stepIndex > 0) setStepIndex((s) => s - 1);
  };

  const handleMinimize = () => {
    if (aiGen.phase === "generating" || aiGen.phase === "done") {
      aiGen.minimize();
    }
  };

  const canGoNext = canContinue() && !isBusy && aiGen.phase === "idle";

  const closeConfirm = showCloseConfirm
    ? createPortal(
        <CloseConfirmDialog
          t={t}
          isRTL={isRTL}
          onStay={() => setShowCloseConfirm(false)}
          onLeave={() => {
            setShowCloseConfirm(false);
            close();
          }}
        />,
        document.body,
      )
    : null;

  if (aiGen.isMinimized) return null;

  if (!show && aiGen.phase === "idle") return null;

  if (phase === "generating") {
    return (
      <>
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#1a1025]/75 p-0 sm:p-6"
          onClick={handleMinimize}
        >
          <div
            dir={isRTL ? "rtl" : "ltr"}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
          >
            <GeneratingView
              t={t}
              isRTL={isRTL}
              pipelineIndex={aiGen.pipelineIndex}
              pipelineDone={aiGen.pipelineDone}
              fillProgress={aiGen.fillProgress}
              onMinimize={handleMinimize}
            />
          </div>
        </div>
        {closeConfirm}
      </>
    );
  }

  if (phase === "done") {
    return (
      <>
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#1a1025]/75 p-0 sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            dir={isRTL ? "rtl" : "ltr"}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
          >
            <DoneView
              t={t}
              isRTL={isRTL}
              error={generationError}
              productId={createdProductId}
              productImage={createdProductImage}
              onClose={close}
              onMinimize={handleMinimize}
              onRetry={handleRetryFromInstructions}
              onGoToPlan={() => {
                close();
                const digital =
                  (account.productMode ?? "classic") === "digital";
                navigate(digital ? "/digital/plan" : "/plan");
              }}
            />
          </div>
        </div>
        {closeConfirm}
      </>
    );
  }

  if (!show) return null;

  if (isSourceStep) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#1a1025]/70 p-0 sm:p-6"
        onClick={requestClose}
      >
        <div
          dir={isRTL ? "rtl" : "ltr"}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-lg bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
        >
          <div className="relative h-44 sm:h-52 overflow-hidden bg-gradient-to-br from-[#9b59bf] to-[#be97d4]">
            <div className="absolute inset-0 opacity-30">
              <div className="absolute -top-10 -end-10 w-40 h-40 rounded-full bg-white/20 blur-2xl" />
              <div className="absolute bottom-0 start-0 w-32 h-32 rounded-full bg-fuchsia-300/30 blur-2xl" />
            </div>
            <img
              src="/landingpageAi.png"
              alt=""
              className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-40"
              draggable={false}
            />
            <button
              onClick={requestClose}
              className="absolute top-4 end-4 z-10 w-9 h-9 rounded-full bg-black/30 text-white flex items-center justify-center backdrop-blur-md hover:bg-black/50 transition-colors"
              aria-label={t("Products.advertorial.close")}
            >
              <X size={18} />
            </button>

            <div className="absolute bottom-5 start-5 end-5">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur-md px-3 py-1 text-[11px] font-medium text-white mb-2 border border-white/20">
                <Wand2 size={12} />
                {t("Products.advertorial.ai_powered")}
              </div>
              <h2 className="text-white text-xl sm:text-2xl font-bold leading-tight drop-shadow-sm">
                {t("Products.advertorial.source_new_title")}
              </h2>
            </div>
          </div>

          <div className="p-5 sm:p-6 overflow-y-auto">
            <p className="text-sm text-gray-500 leading-relaxed mb-5">
              {t("Products.advertorial.step_source_subtitle")}
            </p>

            <button
              onClick={() => chooseSourceAndAdvance("new")}
              className="w-full text-start group rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white p-4 sm:p-5 hover:border-[#9b59bf] hover:shadow-lg hover:shadow-purple-500/15 transition-all duration-300 active:scale-[0.99]"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#9b59bf] text-white flex items-center justify-center shrink-0 shadow-md shadow-purple-500/30 group-hover:scale-105 transition-transform duration-300">
                  <PackagePlus size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 text-[15px] mb-1">
                    {t("Products.advertorial.source_new_product_title")}
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {t("Products.advertorial.source_new_desc")}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-full bg-purple-100 text-[#9b59bf] flex items-center justify-center shrink-0 group-hover:bg-[#9b59bf] group-hover:text-white transition-colors duration-300 mt-1">
                  {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                </div>
              </div>
            </button>
          </div>
        </div>
        {closeConfirm}
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#1a1025]/70 p-0 sm:p-6"
        onClick={requestClose}
      >
        <div
          dir={isRTL ? "rtl" : "ltr"}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-xl bg-white sm:rounded-[28px] rounded-t-[28px] overflow-hidden shadow-2xl flex flex-col max-h-[92vh]"
        >
          <div className="shrink-0 px-5 pt-4 pb-3 flex items-center justify-between gap-3 border-b border-gray-100">
            <button
              onClick={requestClose}
              className="w-9 h-9 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800 flex items-center justify-center transition-colors"
              aria-label={t("Products.advertorial.close")}
            >
              <X size={18} />
            </button>

            <div className="flex items-center gap-1.5 flex-1 justify-center max-w-[200px]">
              {steps.slice(1).map((s, i) => {
                const realIndex = i + 1;
                const done = realIndex < stepIndex;
                const current = realIndex === stepIndex;
                return (
                  <div
                    key={s}
                    className={`h-1.5 flex-1 rounded-full transition-all duration-400 ${
                      done
                        ? "bg-[#9b59bf]"
                        : current
                          ? "bg-[#9b59bf]/70"
                          : "bg-gray-200"
                    }`}
                  />
                );
              })}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] font-semibold text-gray-400 tabular-nums">
                {stepIndex}/{totalSteps - 1}
              </span>
              {!willUseFree && (
                <span className="text-[11px] font-bold text-[#9b59bf] bg-purple-50 border border-purple-100 rounded-full px-2.5 py-1 whitespace-nowrap">
                  {t("Products.advertorial.token_cost", {
                    cost: GENERATION_COST,
                  })}
                </span>
              )}
            </div>
          </div>

          <div
            key={stepKey}
            className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-6"
          >
            {stepKey === "selectProduct" && (
              <SelectProductStep
                form={form}
                setForm={setForm}
                existingProducts={normalizedProducts}
                t={t}
              />
            )}
            {stepKey === "image" && (
              <ImageStep
                form={form}
                fileInputRef={fileInputRef}
                onFileChange={handleFileChange}
                t={t}
              />
            )}
            {stepKey === "name" && (
              <NameStep form={form} setForm={setForm} t={t} />
            )}
            {stepKey === "price" && (
              <PriceStep form={form} setForm={setForm} t={t} />
            )}
            {stepKey === "instructions" && (
              <InstructionsStep
                form={form}
                setForm={setForm}
                toggleSuggestion={toggleSuggestion}
                t={t}
              />
            )}
          </div>

          <div className="shrink-0 px-5 sm:px-6 py-4 border-t border-gray-100 bg-white flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="h-12 px-4 rounded-2xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-1.5"
              >
                {isRTL ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                <span className="hidden sm:inline">
                  {t("Products.advertorial.back")}
                </span>
              </button>

              <button
                onClick={handleNext}
                disabled={!canGoNext}
                className="flex-1 h-12 rounded-2xl bg-[#9b59bf] hover:bg-[#814a9f] disabled:opacity-45 disabled:cursor-not-allowed text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-md shadow-purple-500/25 transition-all active:scale-[0.98]"
              >
                {isInstructionsStep ? (
                  <>
                    <Sparkles size={16} />
                    <span>{t("Products.advertorial.create_page")}</span>
                    {!willUseFree && (
                      <span className="opacity-90 font-bold">
                        {t("Products.advertorial.token_cost", {
                          cost: GENERATION_COST,
                        })}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {t("Products.advertorial.continue")}
                    {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                  </>
                )}
              </button>
            </div>

            {isInstructionsStep && !willUseFree && (
              <p className="text-[11px] leading-relaxed text-center text-red-600 px-1">
                {t("Products.advertorial.cost_note", {
                  cost: GENERATION_COST,
                })}
              </p>
            )}
          </div>
        </div>
      </div>
      {closeConfirm}
    </>
  );
}

function GeneratingView({
  t,
  isRTL,
  pipelineIndex,
  pipelineDone,
  fillProgress,
  onMinimize,
}) {
  return (
    <div className="relative flex flex-col min-h-[420px] sm:min-h-[480px]">
      <AmbientShapes progress={fillProgress} />

      <div className="relative z-10 flex items-center justify-between px-4 pt-4">
        <button
          onClick={onMinimize}
          className="w-9 h-9 rounded-xl bg-white/80 border border-gray-200 text-gray-500 hover:bg-white hover:text-gray-800 flex items-center justify-center transition-colors shadow-sm"
          aria-label={t("Products.advertorial.minimize")}
          title={t("Products.advertorial.minimize")}
        >
          <Minus size={18} />
        </button>
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-purple-50 border border-purple-100 px-8 py-2">
          <Sparkles size={14} className="text-[#9b59bf] animate-pulse" />
          <span className="text-xs font-semibold text-[#9b59bf]">
            {t("Products.advertorial.generating_title")}
          </span>
        </div>

        <ul className="w-full max-w-xs space-y-4">
          {PIPELINE_STEPS.map((step, i) => {
            const isDone = pipelineDone.includes(step.id);
            const isActive = pipelineIndex === i && !isDone;
            const isPending = pipelineIndex < i;

            return (
              <li
                key={step.id}
                className={`flex items-center gap-3 transition-opacity duration-500 ${
                  isPending ? "opacity-40" : "opacity-100"
                }`}
              >
                <PipelineCircle done={isDone} active={isActive} />
                <span
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDone
                      ? "text-emerald-700"
                      : isActive
                        ? "text-gray-900"
                        : "text-gray-400"
                  }`}
                >
                  {t(step.labelKey, { defaultValue: step.defaultLabel })}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function PipelineCircle({ done, active }) {
  return (
    <div
      className={`relative w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-500 ${
        done
          ? "bg-emerald-500 shadow-md shadow-emerald-500/30 border-0"
          : active
            ? "bg-white border-2 border-dashed border-[#9b59bf] animate-[spin_5s_linear_infinite]"
            : "bg-transparent border-2 border-dashed border-gray-300"
      }`}
    >
      {done && <Check size={14} className="text-white" strokeWidth={3} />}
    </div>
  );
}

function AmbientShapes({ progress }) {
  const shapes = [
    {
      size: 56,
      top: "9%",
      left: "25%",
      delay: "0s",
      rotate: 12,
      from: "#9b59bf",
      to: "#c4b5fd",
    },
    {
      size: 40,
      top: "18%",
      right: "14%",
      delay: "0.4s",
      rotate: -8,
      from: "#7c3aed",
      to: "#a78bfa",
    },
    {
      size: 72,
      bottom: "22%",
      left: "8%",
      delay: "0.8s",
      rotate: 20,
      from: "#9b59bf",
      to: "#ddd6fe",
    },
    {
      size: 48,
      bottom: "12%",
      right: "10%",
      delay: "1.2s",
      rotate: -15,
      from: "#7c3aed",
      to: "#c4b5fd",
    },
    {
      size: 36,
      top: "42%",
      left: "6%",
      delay: "0.2s",
      rotate: 5,
      from: "#a78bfa",
      to: "#ddd6fe",
    },
    {
      size: 32,
      top: "50%",
      right: "3%",
      delay: "1s",
      rotate: -22,
      from: "#9b59bf",
      to: "#a78bfa",
    },
  ];

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <style>{`
        @keyframes shapePulse {
          0%, 100% { transform: scale(0.55) rotate(var(--rot)); opacity: 0.25; }
          50% { transform: scale(1) rotate(calc(var(--rot) + 8deg)); opacity: 0.55; }
        }
      `}</style>
      {shapes.map((s, i) => (
        <div
          key={i}
          className="absolute rounded-2xl"
          style={{
            width: s.size,
            height: s.size,
            top: s.top,
            left: s.left,
            right: s.right,
            bottom: s.bottom,
            "--rot": `${s.rotate}deg`,
            animation: `shapePulse 5.5s ease-in-out ${s.delay} infinite`,
          }}
        >
          <div
            className="absolute inset-0 rounded-2xl border-2 border-[#9b59bf]/40"
            style={{ transform: `rotate(${s.rotate}deg)` }}
          />
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              transform: `rotate(${s.rotate}deg)`,
              clipPath: `inset(${Math.max(0, 100 - progress * 100)}% 0 0 0)`,
              transition: "clip-path 0.4s ease-out",
              background: `linear-gradient(to top, ${s.from}99, ${s.to}66)`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

function DoneView({
  t,
  isRTL,
  error,
  productId,
  productImage,
  onClose,
  onMinimize,
  onRetry,
  onGoToPlan,
}) {
  const [imageExpanded, setImageExpanded] = useState(false);
  const isInsufficient = error === "INSUFFICIENT_TOKENS";

  const errorMessage = (() => {
    switch (error) {
      case "INSUFFICIENT_TOKENS":
        return t("Products.advertorial.insufficient_tokens");
      case "NETWORK":
        return t("Products.advertorial.error_network");
      case "TIMEOUT":
        return t("Products.advertorial.error_timeout");
      case "SERVER":
        return t("Products.advertorial.error_server");
      case "GENERIC":
      default:
        return t("Products.advertorial.error_generic");
    }
  })();

  const expandOverlay =
    imageExpanded && productImage
      ? createPortal(
          <div
            className="fixed inset-0 z-[10050] flex flex-col bg-black/95 animate-in fade-in duration-300"
            dir={isRTL ? "rtl" : "ltr"}
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
              <button
                type="button"
                onClick={() => setImageExpanded(false)}
                className="h-11 px-4 rounded-2xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold inline-flex items-center gap-2 backdrop-blur-md border border-white/15 transition-colors"
                aria-label={t("Products.advertorial.minimize_image")}
              >
                <Minimize2 size={16} />
                <span>{t("Products.advertorial.minimize_image")}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setImageExpanded(false);
                  onClose();
                }}
                className="w-11 h-11 rounded-2xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md border border-white/15 transition-colors"
                aria-label={t("Products.advertorial.close")}
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 min-h-0">
              <img
                src={productImage}
                alt={t("Products.advertorial.page_preview_alt")}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
                draggable={false}
              />
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div className="relative flex flex-col min-h-[380px]">
        <div className="relative z-10 flex items-center justify-between px-4 pt-4">
          <button
            onClick={onMinimize}
            className="w-9 h-9 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center transition-colors"
            aria-label={t("Products.advertorial.minimize")}
          >
            <Minus size={18} />
          </button>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center transition-colors"
            aria-label={t("Products.advertorial.close")}
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 text-center">
          {error ? (
            <>
              <p className="text-sm text-red-600 mb-6">{errorMessage}</p>

              {isInsufficient ? (
                <button
                  type="button"
                  onClick={onGoToPlan}
                  className="h-12 px-6 rounded-2xl bg-[#9b59bf] hover:bg-[#814a9f] text-white text-sm font-semibold"
                >
                  {t("Products.advertorial.recharge")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onRetry}
                  className="h-12 px-6 rounded-2xl bg-[#9b59bf] hover:bg-[#814a9f] text-white text-sm font-semibold"
                >
                  {t("Products.advertorial.start_again")}
                </button>
              )}
            </>
          ) : (
            <>
              <div className="relative mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 animate-in zoom-in duration-500">
                  <Check size={30} className="text-white" strokeWidth={2.5} />
                </div>
                <div className="absolute -top-1 -end-1 w-7 h-7 rounded-full bg-amber-400 text-white flex items-center justify-center shadow-md animate-bounce">
                  <PartyPopper size={12} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1.5">
                {t("Products.advertorial.done_title")}
              </h3>
              <p className="text-sm text-gray-500 mb-5 max-w-[280px] leading-relaxed">
                {t("Products.advertorial.done_subtitle")}
              </p>

              {productImage ? (
                <button
                  type="button"
                  onClick={() => setImageExpanded(true)}
                  className="group relative w-full max-w-[260px] rounded-2xl overflow-hidden border-2 border-purple-100 hover:border-[#9b59bf] shadow-md hover:shadow-lg hover:shadow-purple-500/15 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#9b59bf]/40"
                  aria-label={t("Products.advertorial.expand_image")}
                >
                  <div className="aspect-[4/3] bg-gray-50">
                    <img
                      src={productImage}
                      alt={t("Products.advertorial.page_preview_alt")}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      draggable={false}
                    />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-black/55 to-transparent">
                    <span className="inline-flex items-center gap-1.5 text-white text-xs font-medium bg-black/35 backdrop-blur-sm rounded-full px-3 py-1.5">
                      <Maximize2 size={12} />
                      {t("Products.advertorial.expand_image")}
                    </span>
                  </div>
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
      {expandOverlay}
    </>
  );
}

function CloseConfirmDialog({ t, isRTL, onStay, onLeave }) {
  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4">
      <div
        dir={isRTL ? "rtl" : "ltr"}
        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-gray-900 mb-2">
          {t("Products.advertorial.close_confirm_title")}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-5">
          {t("Products.advertorial.close_confirm_body")}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={onStay}
            className="flex-1 h-11 rounded-xl bg-[#9b59bf] hover:bg-[#814a9f] text-white text-sm font-semibold transition-colors"
          >
            {t("Products.advertorial.stay")}
          </button>
          <button
            onClick={onLeave}
            className="flex-1 h-11 rounded-xl border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors"
          >
            {t("Products.advertorial.leave")}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepHeading({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-2xl bg-purple-100 text-[#9b59bf] flex items-center justify-center">
          <Icon size={18} strokeWidth={2.25} />
        </div>
        <h3 className="text-lg font-bold text-gray-900 leading-snug">
          {title}
        </h3>
      </div>
      {subtitle && (
        <p className="text-sm text-gray-500 leading-relaxed ps-[52px] -mt-0.5">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function SelectProductStep({ form, setForm, existingProducts, t }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return existingProducts;
    const q = query.trim().toLowerCase();
    return existingProducts.filter((p) => p.name?.toLowerCase().includes(q));
  }, [existingProducts, query]);

  return (
    <div>
      <StepHeading
        icon={Search}
        title={t("Products.advertorial.step_select_product_title")}
        subtitle={t("Products.advertorial.step_select_product_subtitle")}
      />

      <div className="relative mb-3">
        <Search
          size={16}
          className="absolute top-1/2 -translate-y-1/2 start-3.5 text-gray-400"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Products.advertorial.search_product_placeholder")}
          className="w-full h-11 rounded-xl border border-gray-200 bg-gray-50 ps-10 pe-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 focus:bg-white transition-all"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-400">
          {t("Products.advertorial.no_products_found")}
        </div>
      ) : (
        <div className="max-h-60 overflow-y-auto space-y-2">
          {filtered.map((product) => {
            const selected = form.selectedProduct?.id === product.id;
            return (
              <button
                key={product.id}
                onClick={() =>
                  setForm((p) => ({ ...p, selectedProduct: product }))
                }
                className={`w-full flex items-center gap-3 rounded-xl border p-2.5 text-start transition-all ${
                  selected
                    ? "border-[#9b59bf] bg-purple-50 ring-1 ring-[#9b59bf]/30"
                    : "border-gray-200 hover:border-purple-200 hover:bg-purple-50/40"
                }`}
              >
                <div className="w-11 h-11 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package size={16} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {product.name}
                  </div>
                  {product.price != null && (
                    <div className="text-xs text-gray-500">
                      {product.price} {product.currency || ""}
                    </div>
                  )}
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    selected
                      ? "bg-[#9b59bf] border-[#9b59bf]"
                      : "border-gray-300"
                  }`}
                >
                  {selected && (
                    <Check size={11} className="text-white" strokeWidth={3} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ImageStep({ form, fileInputRef, onFileChange, t }) {
  return (
    <div>
      <StepHeading
        icon={ImagePlus}
        title={t("Products.advertorial.step_image_title")}
        subtitle={t("Products.advertorial.step_image_subtitle")}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFileChange}
        className="hidden"
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className={`w-full rounded-2xl overflow-hidden transition-all duration-300 ${
          form.imagePreview
            ? "ring-2 ring-[#9b59bf]/40"
            : "border-2 border-dashed border-purple-200 hover:border-[#9b59bf] hover:bg-purple-50/50"
        }`}
      >
        {form.imagePreview ? (
          <div className="relative aspect-[4/3] bg-gray-50">
            <img
              src={form.imagePreview}
              alt={t("Products.advertorial.image_preview_alt")}
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/50 to-transparent">
              <span className="inline-flex items-center gap-1.5 text-white text-xs font-medium bg-black/30 backdrop-blur-sm rounded-full px-3 py-1.5">
                <Camera size={12} />
                {t("Products.advertorial.change_photo")}
              </span>
            </div>
          </div>
        ) : (
          <div className="aspect-[4/3] flex flex-col items-center justify-center gap-3 px-4">
            <div className="w-14 h-14 rounded-2xl bg-purple-100 text-[#9b59bf] flex items-center justify-center">
              <Camera size={26} />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-800">
                {t("Products.advertorial.take_photo")}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t("Products.advertorial.image_formats")}
              </p>
            </div>
          </div>
        )}
      </button>
    </div>
  );
}

function NameStep({ form, setForm, t }) {
  return (
    <div>
      <StepHeading
        icon={Type}
        title={t("Products.advertorial.step_name_title")}
        subtitle={t("Products.advertorial.step_name_subtitle")}
      />

      <input
        type="text"
        autoFocus
        value={form.productName}
        onChange={(e) =>
          setForm((p) => ({ ...p, productName: e.target.value }))
        }
        placeholder={t("Products.advertorial.name_placeholder")}
        className="w-full h-14 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-base font-medium text-gray-900 placeholder:text-gray-400 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 focus:bg-white transition-all"
      />
      <p className="mt-2 text-[11px] text-gray-400 ps-1">
        {form.productName.trim().length}/80
      </p>
    </div>
  );
}

function PriceStep({ form, setForm, t }) {
  return (
    <div>
      <StepHeading
        icon={DollarSign}
        title={t("Products.advertorial.step_price_title")}
        subtitle={t("Products.advertorial.step_price_subtitle")}
      />

      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <input
            type="number"
            inputMode="decimal"
            autoFocus
            value={form.price}
            onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
            placeholder="0"
            className="w-full h-14 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-2xl font-bold text-gray-900 tabular-nums placeholder:text-gray-300 placeholder:font-semibold focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 focus:bg-white transition-all"
          />
        </div>
        <div className="h-14 min-w-[72px] px-3 rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center">
          <span className="text-sm font-bold text-[#9b59bf]">
            {form.currency || "DZD"}
          </span>
        </div>
      </div>
    </div>
  );
}

function InstructionsStep({ form, setForm, toggleSuggestion, t }) {
  const activeParts = form.instructions
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div>
      <StepHeading
        icon={MessageSquarePlus}
        title={t("Products.advertorial.step_instructions_title")}
        subtitle={t("Products.advertorial.step_instructions_subtitle")}
      />

      <textarea
        rows={4}
        value={form.instructions}
        onChange={(e) =>
          setForm((p) => ({ ...p, instructions: e.target.value }))
        }
        placeholder={t("Products.advertorial.instructions_placeholder")}
        className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 focus:bg-white transition-all placeholder:text-gray-400 mb-4"
      />

      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
        {t("Products.advertorial.quick_suggestions")}
      </p>
      <div className="flex flex-wrap gap-2">
        {INSTRUCTION_SUGGESTIONS.map((labelKey) => {
          const label = t(labelKey);
          const active = activeParts.includes(label);
          return (
            <button
              key={labelKey}
              type="button"
              onClick={() => toggleSuggestion(labelKey)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                active
                  ? "bg-[#9b59bf] text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-purple-50 hover:text-[#9b59bf]"
              }`}
            >
              {active && <Check size={12} strokeWidth={3} />}
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
